import type { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
// mantine
import { Alert, Tabs } from '@mantine/core';
// icons
import { CircleX } from 'lucide-react';
// components
import Header from '@/components/Layout/components/Header';
import AccountForm from '@/pages/Profile/components/AccountForm';
import LLMKeys from '@/pages/Profile/components/LLMKeys';
import Advanced from '@/pages/Profile/components/Advanced';
import Members from '@/pages/Profile/components/Members';
// utils
import { useProfileQuery } from '@/services/customer/profile/profile.queries.ts';
import { PATHS } from '@/const';
import { PageContainer } from '@/components/PageContainer/PageContainer.tsx';
import { ProfileTab } from '@/pages/Profile/Profile.const';

type ProfileProps = {
    activeTab?: ProfileTab;
};

const Profile: FC<ProfileProps> = ({ activeTab = ProfileTab.Account }) => {
    const { data: profile } = useProfileQuery();
    const navigate = useNavigate();

    return (
        <div className="w-full">
            <Helmet>
                <title>NEWO - Profile</title>
            </Helmet>
            <Header breadcrumbs={[{ label: 'Profile' }]} />

            <PageContainer className="pt-6" maw={1200}>
                {profile?.is_active === false && (
                    <Alert className="mb-4" color="red" icon={<CircleX />}>
                        <div className="text-red-8">Account deactivated</div>
                    </Alert>
                )}
                <Tabs
                    keepMounted={false}
                    value={activeTab}
                    onChange={async (value) =>
                        navigate(`${PATHS.profile.root}/${value}`)
                    }
                >
                    <Tabs.List>
                        <Tabs.Tab value={ProfileTab.Account}>Profile</Tabs.Tab>
                        <Tabs.Tab value={ProfileTab.Members}>Members</Tabs.Tab>
                        <Tabs.Tab value={ProfileTab.Keys}>LLM Keys</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel className="pt-6" value={ProfileTab.Account}>
                        {profile ? (
                            <AccountForm profile={profile} />
                        ) : undefined}
                    </Tabs.Panel>
                    <Tabs.Panel className="pt-6" value={ProfileTab.Keys}>
                        <LLMKeys />
                    </Tabs.Panel>
                    <Tabs.Panel className="pt-6" value={ProfileTab.Advanced}>
                        {profile ? <Advanced profile={profile} /> : undefined}
                    </Tabs.Panel>
                    <Tabs.Panel className="pt-6" value={ProfileTab.Members}>
                        <Members />
                    </Tabs.Panel>
                </Tabs>
            </PageContainer>
        </div>
    );
};

export default Profile;
